import { useMemo } from 'react';

export type DeptUserOption = { value: string; label: string };
type AnyUser = any;

const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();
const upper = (s: any) => norm(s).toUpperCase();
const teamToken = (s: any): string => upper(s).replace(/\s+/g, '');
const compactToken = (s: any): string => upper(s).replace(/\s+/g, '');

const cleanText = (s: any): string => {
  const value = norm(s);
  const upperValue = value.toUpperCase();
  if (
    !value ||
    upperValue === 'NULL' ||
    upperValue === '[NULL]' ||
    upperValue === 'NONE' ||
    upperValue === '[NONE]' ||
    upperValue === 'UNDEFINED' ||
    upperValue === '[UNDEFINED]'
  ) {
    return '';
  }
  return value;
};

const firstCleanText = (...values: any[]): string => {
  for (const value of values) {
    const text = cleanText(value);
    if (text) return text;
  }
  return '';
};

const uniqueOptions = (opts: DeptUserOption[]) => {
  const seen = new Set<string>();
  return (opts ?? []).filter((o) => {
    const v = norm(o?.value);
    const l = norm(o?.label);
    if (!v && !l) return false;
    const k = `${v}__${l}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const getUserDept = (u: AnyUser): string => {
  return firstCleanText(
    u?.department,
    u?.department_code,
    u?.department_name,
    u?.dept,
    u?.dept_code,
    u?.dept_name
  );
};

const getUserTeam = (u: AnyUser): string => {
  return firstCleanText(
    // preferred_team_* を通常所属チームより優先する
    u?.preferred_team_label,
    u?.preferredTeamLabel,
    u?.preferred_team?.team_label,
    u?.preferredTeam?.teamLabel,
    u?.preferred_team_id,
    u?.preferredTeamId,
    u?.preferred_team?.team_id,
    u?.preferredTeam?.teamId,

    // preferred_team_* が無い場合だけ通常所属チームを使う
    u?.team,
    u?.team_name,
    u?.teamName,
    u?.team_label,
    u?.teamLabel
  );
};

const getUserEmail = (u: AnyUser): string => {
  return norm(
    u?.mail_address ??
      u?.email ??
      u?.mail ??
      u?.user_id ??
      u?.userId ??
      u?.id ??
      ''
  );
};

const getUserDisplayName = (u: AnyUser): string => {
  const last = norm(u?.last_name ?? u?.family_name ?? u?.familyName ?? '');
  const first = norm(u?.first_name ?? u?.given_name ?? u?.givenName ?? '');
  const full = norm([last, first].filter(Boolean).join(' '));
  if (full) return full;

  return norm(
    u?.label ??
      u?.name ??
      u?.user_name ??
      u?.userName ??
      u?.display_name ??
      u?.displayName ??
      u?.full_name ??
      u?.fullName ??
      u?.username ??
      getUserEmail(u)
  );
};

const toUserOption = (u: AnyUser): DeptUserOption => {
  const value = getUserEmail(u) || norm(u?.value ?? u?.id ?? '');
  const label = getUserDisplayName(u) || value;
  return { value, label };
};

const getTeamEntityId = (x: any): string =>
  norm(
    x?.team_id ??
      x?.teamId ??
      x?.id ??
      x?.value ??
      ''
  );

const getTeamEntityLabel = (x: any): string =>
  norm(
    x?.label ??
      x?.team_name ??
      x?.teamName ??
      x?.team_label ??
      x?.teamLabel ??
      x?.name ??
      x?.value ??
      ''
  );

const appendUniqueTeam = (
  out: string[],
  seen: Set<string>,
  raw: any,
  options?: { allowNumeric?: boolean }
) => {
  const s = cleanText(raw);
  if (!s) return;
  if (/^\d+$/.test(s) && !options?.allowNumeric) return;
  const key = teamToken(s);
  if (!key || seen.has(key)) return;
  seen.add(key);
  out.push(s);
};

type NormalizedMember = { value: string; label: string };

const normalizeSelectedMembers = (selectedMembers: any[] | null | undefined): NormalizedMember[] => {
  const list = Array.isArray(selectedMembers) ? selectedMembers : [];
  const out: NormalizedMember[] = [];
  const seen = new Set<string>();

  const push = (valueRaw: any, labelRaw: any) => {
    const value = norm(valueRaw);
    const label = norm(labelRaw);
    const resolvedValue = value || label;
    const resolvedLabel = label || value || resolvedValue;
    if (!resolvedValue && !resolvedLabel) return;
    const key = `${resolvedValue}__${resolvedLabel}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ value: resolvedValue, label: resolvedLabel });
  };

  list.forEach((item) => {
    if (item == null) return;

    if (typeof item === 'string' || typeof item === 'number') {
      push(item, item);
      return;
    }

    const last = norm(item?.last_name ?? item?.lastName ?? item?.family_name ?? item?.familyName ?? '');
    const first = norm(item?.first_name ?? item?.firstName ?? item?.given_name ?? item?.givenName ?? '');
    const fullName = norm([last, first].filter(Boolean).join(' '));

    const value = norm(
      item?.value ??
        item?.mail_address ??
        item?.mailAddress ??
        item?.email ??
        item?.mail ??
        item?.user_id ??
        item?.userId ??
        item?.id ??
        ''
    );

    const label =
      fullName ||
      norm(
        item?.label ??
          item?.name ??
          item?.user_name ??
          item?.userName ??
          item?.display_name ??
          item?.displayName ??
          item?.full_name ??
          item?.fullName ??
          value
      );

    push(value, label);
  });

  return out;
};

const inferPreferredTeamsFromMembers = ({
  users,
  selectedMembers,
}: {
  users: AnyUser[] | null | undefined;
  selectedMembers: any[] | null | undefined;
}): string[] => {
  const list = Array.isArray(users) ? users : [];
  const members = normalizeSelectedMembers(selectedMembers);
  if (list.length === 0 || members.length === 0) return [];

  const teamByValue = new Map<string, string>();
  const labelStats = new Map<string, { count: number; team: string }>();

  list.forEach((u) => {
    const team = norm(getUserTeam(u));
    if (!team) return;

    const valueKey = upper(getUserEmail(u));
    if (valueKey && !teamByValue.has(valueKey)) {
      teamByValue.set(valueKey, team);
    }

    const labelKey = compactToken(getUserDisplayName(u));
    if (!labelKey) return;

    const prev = labelStats.get(labelKey);
    if (!prev) {
      labelStats.set(labelKey, { count: 1, team });
      return;
    }

    labelStats.set(labelKey, { count: prev.count + 1, team: prev.team });
  });

  const out: string[] = [];
  const seen = new Set<string>();

  members.forEach((m) => {
    const valueKey = upper(m.value);
    const labelKey = compactToken(m.label || m.value);

    let team = '';
    if (valueKey && teamByValue.has(valueKey)) {
      team = norm(teamByValue.get(valueKey));
    } else if (labelKey) {
      const stat = labelStats.get(labelKey);
      if (stat?.count === 1) {
        team = norm(stat.team);
      }
    }

    appendUniqueTeam(out, seen, team, { allowNumeric: true });
  });

  return out;
};

type ResolveTeamCandidatesArgs = {
  selectedTeam?: string | number | null;
  teamOptions?: any[] | null | undefined;
  teams?: any[] | null | undefined;
};

const resolveSelectedTeamCandidates = ({
  selectedTeam,
  teamOptions,
  teams,
}: ResolveTeamCandidatesArgs): string[] => {
  const selected = norm(selectedTeam);
  if (!selected) return [];

  const out: string[] = [];
  const seen = new Set<string>();
  const selectedUpper = upper(selected);
  const selectedCompact = compactToken(selected);

  appendUniqueTeam(out, seen, selected, { allowNumeric: true });

  const optionsList = Array.isArray(teamOptions) ? teamOptions : [];
  optionsList.forEach((opt) => {
    const id = getTeamEntityId(opt);
    const label = getTeamEntityLabel(opt);
    const hitById = id !== '' && upper(id) === selectedUpper;
    const hitByLabel = label !== '' && (upper(label) === selectedUpper || compactToken(label) === selectedCompact);
    if (!(hitById || hitByLabel)) return;

    appendUniqueTeam(out, seen, id, { allowNumeric: true });
    appendUniqueTeam(out, seen, label);
    appendUniqueTeam(out, seen, opt?.team_name);
    appendUniqueTeam(out, seen, opt?.teamName);
    appendUniqueTeam(out, seen, opt?.team_label);
    appendUniqueTeam(out, seen, opt?.teamLabel);
    appendUniqueTeam(out, seen, opt?.name);
  });

  const teamsList = Array.isArray(teams) ? teams : [];
  teamsList.forEach((row) => {
    const id = getTeamEntityId(row);
    const label = getTeamEntityLabel(row);
    const hitById = id !== '' && upper(id) === selectedUpper;
    const hitByLabel = label !== '' && (upper(label) === selectedUpper || compactToken(label) === selectedCompact);
    if (!(hitById || hitByLabel)) return;

    appendUniqueTeam(out, seen, id, { allowNumeric: true });
    appendUniqueTeam(out, seen, label);
    appendUniqueTeam(out, seen, row?.team_name);
    appendUniqueTeam(out, seen, row?.teamName);
    appendUniqueTeam(out, seen, row?.team_label);
    appendUniqueTeam(out, seen, row?.teamLabel);
    appendUniqueTeam(out, seen, row?.name);
  });

  return out;
};

export type BuildPreferredTeamCandidatesArgs = {
  users: AnyUser[] | null | undefined;
  selectedTeam?: string | number | null;
  teamOptions?: any[] | null | undefined;
  teams?: any[] | null | undefined;
  selectedMembers?: any[] | null | undefined;
  fallbackTeamCandidates?: Array<string | null | undefined>;
};

export const buildPreferredTeamCandidates = ({
  users,
  selectedTeam,
  teamOptions,
  teams,
  selectedMembers,
  fallbackTeamCandidates = [],
}: BuildPreferredTeamCandidatesArgs): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();

  resolveSelectedTeamCandidates({ selectedTeam, teamOptions, teams }).forEach((teamName) => {
    appendUniqueTeam(out, seen, teamName);
  });

  inferPreferredTeamsFromMembers({ users, selectedMembers }).forEach((teamName) => {
    appendUniqueTeam(out, seen, teamName);
  });

  (fallbackTeamCandidates ?? []).forEach((teamName) => {
    appendUniqueTeam(out, seen, teamName);
  });

  return out;
};

export type UseDepartmentUserOptionsArgs = {
  users: AnyUser[] | null | undefined;
  deptCandidates: Array<string | null | undefined>;
  strict?: boolean;
  preferredTeamCandidates?: Array<string | null | undefined>;
  prioritizePreferredTeam?: boolean;
};

export const useDepartmentUserOptions = ({
  users,
  deptCandidates,
  strict = true,
  preferredTeamCandidates = [],
  prioritizePreferredTeam = false,
}: UseDepartmentUserOptionsArgs): DeptUserOption[] => {
  return useMemo(() => {
    const list = Array.isArray(users) ? users : [];

    const keys = (deptCandidates ?? [])
      .map((x) => upper(x))
      .filter(Boolean);

    const uniqKeys = Array.from(new Set(keys));

    if (uniqKeys.length === 0) {
      if (strict) return [];
      return uniqueOptions(list.map(toUserOption));
    }

    const filtered = list.filter((u) => {
      const d = upper(getUserDept(u));
      return d !== '' && uniqKeys.includes(d);
    });

    const preferredTeamKeys = Array.from(
      new Set((preferredTeamCandidates ?? []).map((x) => teamToken(x)).filter(Boolean))
    );

    let ordered = filtered;
    if (prioritizePreferredTeam && preferredTeamKeys.length > 0) {
      const preferredSet = new Set(preferredTeamKeys);
      const matched: AnyUser[] = [];
      const others: AnyUser[] = [];
      filtered.forEach((u) => {
        const tk = teamToken(getUserTeam(u));
        if (tk && preferredSet.has(tk)) matched.push(u);
        else others.push(u);
      });
      ordered = [...matched, ...others];
    }

    return uniqueOptions(ordered.map(toUserOption));
  }, [
    users,
    deptCandidates,
    strict,
    preferredTeamCandidates,
    prioritizePreferredTeam,
  ]);
};
